import React, { useEffect, useState } from 'react';
import { EaseDrawer } from '../../../libs/EaseDrawer';
import { Descriptions, Badge, Typography, Popconfirm, Icon, Button, Select, DatePicker, Tooltip } from 'antd';
import { getBankLogo, PAYMENT_MODES, TRANSFER_STATUS_TYPES } from '../../../constants';
import { Text1, Text3, Text4, Text5 } from '../../../styles';
import { alertify, printRupee, handleApiExceptions, getObjectFromArray, isVirtualAccountVisible } from '../../../app_methods';
import moment from 'moment';
import './index.scss'
import { TransferRequestQueryModal } from '../../Common/TransferRequestQueryModal';
import { PermissionHoc } from '../../HOC';
import { TransferRequest } from '../../../models';
import { beneficiaryOrganisationTypes } from '../../../constants';

const PermissibleButton = PermissionHoc(Button, "", { is_component: true, fallbackComponent: null })

const TransferInfo = (props) => {
    const [showTransferQueryModal, setTransferQueryModal] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [isVAEditing, setIsVAEditing] = useState(false);
    const [isScheduledDateEditing, setIsScheduledDateEditing] = useState(false);
    const [virtualAccount, setVirtualAccount] = useState(props.transferRequest.virtual_account_id);
    const [scheduledDate, setScheduledDate] = useState(moment(props.transferRequest.scheduled_for, 'YYYY-MM-DD'));
    const [isReTriggeringWebHook, setIsReTriggeringWebHook] = useState(false);
    const triggerWebhook = getObjectFromArray(props.merchant?.webhook_settings|| [], "event_name", "TRANSFER_STATUS_UPDATE") || {};

    useEffect(() => {
        setVirtualAccount(props.transferRequest.virtual_account_id);
        setScheduledDate(moment(props.transferRequest.scheduled_for, 'YYYY-MM-DD'));
    }, [props.transferRequest])

    const calculateGST = () => {
        if (!props.transferRequest.gst_amount || !props.transferRequest.service_charge) return 0;
        return ((props.transferRequest.gst_amount * 100) / props.transferRequest.service_charge).toFixed(3);
    };

    const toggleTransferQueryModal = () => {
        setTransferQueryModal(!showTransferQueryModal);
    };

    const sendRequestQuery = async (data = {}) => {
        setIsSaving(true);
        const response = await TransferRequest.sendQueryData(data)
        if (!response.success) {
            setIsSaving(false);
            handleApiExceptions(response);
            return;
        }
        toggleTransferQueryModal(false)
        setIsSaving(false);
        let message = 'Your issue has been raised with our internal team and we will inform you on your email ' + props.merchant.email + ' for any update.'
        alertify("Success", message, "success");
    }

    const reTriggerWebhook = async () => {
        setIsReTriggeringWebHook(true);
        const transfer_request = new TransferRequest(props.transferRequest);
        const response = await transfer_request.retrigger_webhook();
        if (!response.success) {
            setIsReTriggeringWebHook(false);
            handleApiExceptions(response);
            return;
        }
        setIsReTriggeringWebHook(false);
        let message = response.data.message
        alertify("Success", message, "success");
    }

    const updateTransferRequest = async (updateField) => {
        setIsSaving(true);
        let queryData = {};
        let filters=props.filters
        if (updateField == "virtual_account") {
            queryData.virtual_account_id = virtualAccount;
            filters={...filters,"virtual_account":virtualAccount}
        }
        else if (updateField == "scheduled_for") {
            queryData.scheduled_for = scheduledDate.format("YYYY-MM-DD");
        }
        const response = await props.transferRequest.update(queryData);
        if (!response.success) {
            setIsSaving(false);
            handleApiExceptions(response.message)
            return;
        }
        props.fetchTransfers(props.pagination , filters)
        updateField == "virtual_account" ? setIsVAEditing(false) : setIsScheduledDateEditing(false)
        setIsSaving(false);
    }

    const initiateTransfer = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        const response = await props.transferRequest.initiate_scheduled_transfer();
        if (!response.success) {
            setIsSaving(false);
            handleApiExceptions(response.message)
            return;
        }
        alertify("Success", "Transfer Initiated Successfully", "success");
        props.fetchTransfers()
        props.onClose()
        setIsSaving(false);
    }

    const onDrawerClose = () => {
        setIsVAEditing(false)
        setIsScheduledDateEditing(false)
        props.onClose()
    }

    function disabledDate(current) {
        return current && current < moment().endOf('day');
    }

    function onDateChange(date, dateString) {
        setScheduledDate(dateString ? moment(dateString) : null);
    }

    const renderTitle = () => {
        return (
            <div className={`${props.transferRequest.is_submerchant ? 'mb-4' : 'mb-1'}`}>
                <Text1 className="d-block" strong>Transfer Details</Text1>
                <Text5 copyable>{props.transferRequest.id}</Text5>
            </div>
        )
    };

    return (
        <EaseDrawer
            className="transfer-request-details-wrapper"
            visible={props.visible}
            onClose={!isSaving && onDrawerClose}
            hideHeader
            hideFooter
            width={570}
            maskClosable={!isSaving}
        >
            <div className="mt-3">
                {renderTitle()}
                <div className='float-right mb-2'>
                    {(props.showQuery && !props.transferRequest.is_submerchant && ["success", "accepted", "queued", "in_process", "pending", "on_hold"].includes(props.transferRequest.status)) && <PermissibleButton required_permission="TRANSFERS_RAISE_QUERY" size='small' onClick={() => toggleTransferQueryModal(props.transferRequest.id)}><Icon type="question-circle" theme="twoTone" twoToneColor="#d4b106" /> Raise Query </PermissibleButton>}
                    {(props?.merchant?.notification_setting?.webhook_enabled && triggerWebhook?.is_enable && !props.transferRequest.is_submerchant) && <PermissibleButton required_permission="TRANSFERS_WRITE" className='ml-2' size='small' icon='code' loading={isReTriggeringWebHook} onClick={() => reTriggerWebhook()}> Trigger Webhook </PermissibleButton>}
                </div>
                <Descriptions className="mb-4" title={false} column={1} bordered size="small">
                    <Descriptions.Item label="Source Virtual Account" className="">
                        {props.transferRequest && isVAEditing ?
                            <div className="d-flex align-items-center">
                                <Select style={{ width: 200 }} defaultValue={props.transferRequest.virtual_account_id} onChange={e => setVirtualAccount(e)} disabled={isSaving}>
                                    {
                                        props.merchant.virtual_accounts.filter(va => va.status == "active" && isVirtualAccountVisible(va)).map((va, index) => {
                                            return (
                                                <Select.Option value={va.id} disabled={va.funding_via == 2 && !props.merchant.allow_payouts_via_cc_va ? true : false}>
                                                    <div className="d-flex">
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                width: 28,
                                                                overflow: "hidden",
                                                                textAlign: "center",
                                                                marginRight: 8
                                                            }}
                                                        >
                                                            <img
                                                                style={{
                                                                    margin: "auto",
                                                                    width: "auto",
                                                                    maxWidth: "100%",
                                                                    maxHeight: 28
                                                                }}
                                                                alt=""
                                                                src={getBankLogo(va.provider_code)}
                                                            />
                                                        </div>
                                                        <Text4 className="mr-1">{va.label}</Text4>
                                                    </div>
                                                </Select.Option>
                                            )
                                        })
                                    }
                                </Select>
                                <div className="d-flex justify-content-end align-items-center">
                                    <Tooltip placement="top" title={"Update Transfer"}>
                                        <PermissibleButton required_permission="TRANSFERS_WRITE" className="ml-2" onClick={(e) => updateTransferRequest("virtual_account")} icon="check" size="small" />
                                    </Tooltip>

                                    <Tooltip placement="top" title={"Cancel"}>
                                        <PermissibleButton required_permission="TRANSFERS_WRITE" className="ml-2" onClick={(e) => setIsVAEditing(false)} icon="close" size="small" />
                                    </Tooltip>
                                </div>
                            </div>
                            :
                            <div className="d-flex align-items-center">
                                {props.transferRequest.source_virtual_account}
                                <div className={props.transferRequest.status === "scheduled" ? "" : "d-none"}>
                                    <Tooltip placement="top" title={"Update Virtual Account"}>
                                        <PermissibleButton required_permission="TRANSFERS_WRITE" onClick={() => setIsVAEditing(true)} className="ml-2" icon="edit" disabled={isSaving} />
                                    </Tooltip>
                                </div>
                            </div>
                        }
                    </Descriptions.Item>
                    <Descriptions.Item label="Unique Request Number">{props.transferRequest.unique_request_number}</Descriptions.Item>
                    <Descriptions.Item label="Created At">{props.transferRequest.created_at && moment(props.transferRequest.created_at).format("LLL")}</Descriptions.Item>
                    {
                        props.transferRequest && props.transferRequest.scheduled_for ?
                            <Descriptions.Item label="Scheduled For" className="">
                                {isScheduledDateEditing ?
                                    <div className="d-flex align-items-center">
                                        <DatePicker
                                            style={{ width: "70%" }}
                                            format="YYYY-MM-DD"
                                            disabledDate={disabledDate}
                                            onChange={onDateChange}
                                            value={scheduledDate}
                                            allowClear={false}
                                        />
                                        <div className="d-flex justify-content-end mr-1">
                                            <Tooltip placement="top" title={"Update Transfer"}>
                                                <PermissibleButton required_permission="TRANSFERS_WRITE" className="ml-2" onClick={() => updateTransferRequest("scheduled_for")} icon="check" size="small" />
                                            </Tooltip>

                                            <Tooltip placement="top" title={"Cancel"}>
                                                <PermissibleButton required_permission="TRANSFERS_WRITE" className="ml-2" onClick={(e) => setIsScheduledDateEditing(false)} icon="close" size="small" />
                                            </Tooltip>
                                        </div>
                                    </div>

                                    : <div className="d-flex justify-content-start align-items-center">
                                        {props.transferRequest.scheduled_for && moment(props.transferRequest.scheduled_for).format("LL")}
                                        <div className={props.transferRequest.status === "scheduled" ? "" : "d-none"}>
                                            <Tooltip placement="bottom" title={"Update Scheduled Date"}>
                                                <PermissibleButton required_permission="TRANSFERS_WRITE" onClick={() => setIsScheduledDateEditing(true)} className="ml-2" icon="edit" disabled={isSaving} />
                                            </Tooltip>

                                            <Popconfirm placement="topRight" title="Are you sure you want to initiate this transfer now?" okText="Yes" cancelText="No" onConfirm={(e) => initiateTransfer(e)}>
                                                <Tooltip placement="bottom" title={"Initiate Transfer Now"}>
                                                    <PermissibleButton required_permission="TRANSFERS_WRITE" className="ml-1" icon="play-circle" loading={isSaving} disabled={isSaving} />

                                                </Tooltip>
                                            </Popconfirm>
                                        </div>
                                    </div>
                                }
                            </Descriptions.Item>
                            : null
                    }
                    <Descriptions.Item label="Amount">{printRupee(props.transferRequest.amount)}</Descriptions.Item>
                    <Descriptions.Item label="Payment Mode">{PAYMENT_MODES[props.transferRequest.payment_mode]}</Descriptions.Item>
                    {
                        props.transferRequest.transfer_date ?
                            <Descriptions.Item label="Transfer Date">
                                {props.transferRequest.transfer_date && moment(props.transferRequest.transfer_date).format("LLL")}
                            </Descriptions.Item> : null
                    }
                    {
                        props.transferRequest.success_at ?
                            <Descriptions.Item label="Success At">
                                {props.transferRequest.success_at && moment(props.transferRequest.success_at).format("LLL")}
                            </Descriptions.Item> : null
                    }
                    {
                        props.transferRequest.status ?
                            <Descriptions.Item label="Status">
                                <div className="w-100 d-flex justify-content-between align-items-center">
                                    <Badge
                                        color={TRANSFER_STATUS_TYPES[props.transferRequest.status].color}
                                        text={<Text4>{TRANSFER_STATUS_TYPES[props.transferRequest.status].label}</Text4>}
                                    />
                                    <div className="w-60 d-flex justify-content-between align-items-center">
                                        {
                                            ["queued", "unapproved", "scheduled"].includes(props.transferRequest.status) ?
                                                <Popconfirm title="Are you sure you want to cancel this transfer?" okText="Yes" cancelText="No" onConfirm={() => props.cancelTransfer(props.transferRequest)}>
                                                    <PermissibleButton required_permission="TRANSFERS_WRITE" size="small" type="danger" ghost loading={props.isCancelling} disabled={isSaving}>Cancel</PermissibleButton>
                                                </Popconfirm> : null
                                        }
                                    </div>
                                </div>
                            </Descriptions.Item>
                            :
                            null
                    }
                    {
                        (props.transferRequest.status == 'rejected' || props.transferRequest.status == 'failure' || props.transferRequest.status == 'reversed') && props.transferRequest.failure_reason ?
                            <Descriptions.Item label="Failure Reason">{props.transferRequest.failure_reason || '---'}</Descriptions.Item>
                            :
                            null

                    }
                    <Descriptions.Item label="Unique Transaction Reference">{props.transferRequest.unique_transaction_reference || "---"}</Descriptions.Item>
                    <Descriptions.Item label="Narration">{props.transferRequest.narration || "---"}</Descriptions.Item>
                    <Descriptions.Item label="Beneficiary Name">{props.transferRequest.beneficiary_account_name || "---"}</Descriptions.Item>
                    {
                        props.transferRequest.payment_mode == "UPI" || props.transferRequest.payment_mode === "AMAZON_WALLET"  ?
                            <Descriptions.Item label="UPI Handle">{props.transferRequest.beneficiary_upi_handle || "---"}</Descriptions.Item>
                            :
                            props.transferRequest.payment_mode == "PAYTM" ?
                                <Descriptions.Item label="Account Number">{props.transferRequest.beneficiary_account_number || "---"}</Descriptions.Item>
                                :
                                <>
                                    <Descriptions.Item label="Account Number">{props.transferRequest.beneficiary_account_number || "---"}</Descriptions.Item>
                                    <Descriptions.Item label="IFSC Code">{props.transferRequest.beneficiary_account_ifsc?.toUpperCase() || "---"}</Descriptions.Item>
                                    <Descriptions.Item label="Bank Name">{props.transferRequest.beneficiary_bank_name || "---"}</Descriptions.Item>
                                </>
                    }

                    <Descriptions.Item label="Platform Charges">{props.transferRequest?.service_charge? printRupee(props.transferRequest?.service_charge, 3) : printRupee(0) }</Descriptions.Item>
                    <Descriptions.Item label="GST">{props.transferRequest?.gst_amount ? printRupee(props.transferRequest?.gst_amount, 3) :printRupee(0) }</Descriptions.Item>
                    <Descriptions.Item label="Total Charges">{props.transferRequest?.service_charge_with_gst ? printRupee(props.transferRequest?.service_charge_with_gst, 3): printRupee(0)}</Descriptions.Item>
                </Descriptions>

                <Text3 className="d-block mt-4 mb-2" strong>Additional Details</Text3>
                <Descriptions title={false} column={1} bordered size="small">
                    <Descriptions.Item label="UDF 1">{props.transferRequest.udf1 || "---"}</Descriptions.Item>
                    <Descriptions.Item label="UDF 2">{props.transferRequest.udf2 || "---"}</Descriptions.Item>
                    <Descriptions.Item label="UDF 3">{props.transferRequest.udf3 || "---"}</Descriptions.Item>
                    <Descriptions.Item label="UDF 4">{props.transferRequest.udf4 || "---"}</Descriptions.Item>
                    <Descriptions.Item label="UDF 5">{props.transferRequest.udf5 || "---"}</Descriptions.Item>
                    {props.transferRequest.request_meta?.yesbank_ftx_gaming && 
                      <>
                        <Descriptions.Item label="User ID">{props.transferRequest.request_meta?.user_id || "---"}</Descriptions.Item>
                        <Descriptions.Item label="PAN Number">{props.transferRequest.request_meta?.pan_number || "---"}</Descriptions.Item>
                      </>
                    }
                    {props.transferRequest.request_meta?.yesbank_ftx && 
                      <>
                        <Descriptions.Item label="PAN Number">{props.transferRequest.request_meta?.pan_number || "---"}</Descriptions.Item>
                        <Descriptions.Item label="GST Number">{props.transferRequest.request_meta?.gst_number || "---"}</Descriptions.Item>
                        <Descriptions.Item label="Business Model">{props.transferRequest.request_meta?.business_model || "---"}</Descriptions.Item>
                        <Descriptions.Item label="Cin Number">{props.transferRequest.request_meta?.cin_number || "---"}</Descriptions.Item>
                        <Descriptions.Item label="Organisation Type">
                            {beneficiaryOrganisationTypes.find(x => x.value === props.transferRequest.request_meta?.organisation_type)?.label || "---"}
                        </Descriptions.Item>
                      </>
                    }
                </Descriptions>
                <TransferRequestQueryModal
                    visible={showTransferQueryModal}
                    toggleTransferQueryModal={toggleTransferQueryModal}
                    transferRequest={props.transferRequest}
                    isSaving={isSaving}
                    onSubmit={sendRequestQuery}
                />
            </div>
        </EaseDrawer>
    )
};

TransferInfo.defaultProps = {
    showQuery: true,
    transferRequest: {
        is_submerchant: false
    }

}

export { TransferInfo };